<template>
  <div
    v-if="FF_DISPLAY_PERSONALIZED_FEED_DEBUG_INFO"
    class="mb-12 flex flex-wrap gap-8"
  >
    <div>cards: {{ cards.length }}</div>

    <div>pending {{ pending }}</div>
    <div>activeTabIndex: {{ activeTabIndex }}</div>
  </div>

  <Background
    v-if="pending || cards?.length"
    :background
    :class="{
      'p-16 md:p-24 rounded-lg': !!background,
    }"
    container-class="!items-start"
  >
    <RevSkeleton
      v-if="forceSingleContent && pending"
      class="mb-16"
      height="32px"
      shape="rectangle"
      width="206px"
    />
    <BlockTitle v-else :title="mainTitle" />

    <div
      v-if="!forceSingleContent"
      ref="tabs-scroller"
      class="mb-20 mt-4 flex grow flex-col justify-between overflow-auto scrollbar-none max-w-full"
      :class="maskClass"
    >
      <div
        :aria-label="title || ''"
        class="flex w-max items-start gap-8 overflow-x-auto overflow-y-hidden scrollbar-none max-w-full"
        role="tablist"
      >
        <template v-if="pending && !forceSingleContent">
          <RevSkeleton height="40px" shape="rectangle" width="130px" />
          <RevSkeleton height="40px" shape="rectangle" width="130px" />
        </template>

        <RevChip
          v-for="(tab, tabIndex) in cards"
          :id="`tab-${tabIndex}`"
          :key="tab.id"
          ref="tabItem"
          aria-controls="content-card-panel"
          :aria-selected="activeTabIndex === tabIndex"
          class="shrink-0"
          :data-test="`tab-${tabIndex}`"
          :end-icon="tab.icon"
          :label="tab.title"
          :model-value="activeTabIndex === tabIndex"
          role="tab"
          :tabindex="activeTabIndex === tabIndex ? 0 : -1"
          @click="() => onTabClick(tabIndex)"
          @keydown.left.prevent="goToPreviousTab"
          @keydown.right.prevent="goToNextTab"
        />
      </div>
    </div>

    <div
      id="content-card-panel"
      ref="content-card-panel"
      :aria-labelledby="
        forceSingleContent ? undefined : `tab-${activeTabIndex}`
      "
      class="h-[408px] md:h-[394px] w-full"
      data-test="content-card-panel"
      :role="forceSingleContent ? undefined : 'tabpanel'"
    >
      <CardCollectionSkeleton v-if="pending" layout="carousel" />
      <component
        :is="currentContentCard?.tag || null"
        v-else-if="hydrationCompleted"
        v-bind="currentContentCard?.props"
        :key="currentContentCard?.id"
      />
    </div>
  </Background>
</template>

<script lang="ts" setup>
import { useRuntimeConfig } from '#imports'
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue'

import type { PersonalizedFeedApiProps } from '@backmarket/http-api/src/api-specs-content/models/personalized-feed'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevChip } from '@ds/components/Chip'
import { RevSkeleton } from '@ds/components/Skeleton'

import { useScrollMaskClass } from '../../composables/useScrollMaskClass'
import type { BlockProps } from '../../models/content-block'
import Background from '../../shared-components/Background/Background.vue'
import BlockTitle from '../../shared-components/BlockTitle/BlockTitle.vue'
import CardCollectionSkeleton from '../../shared-components/CardCollectionSkeleton/CardCollectionSkeleton.vue'

import { useIsVisible } from './composables/useIsVisible'
import { usePersonalizedFeed } from './composables/usePersonalizedFeed'
import { useActiveTabIndex } from './composables/useTabIndex'

export type PersonalizedFeedBlockProps = PersonalizedFeedApiProps & BlockProps

const {
  feed,
  fallback,
  blockPosition = 1,
  title,
  forceSingleContent,
  productQuery,
  tracking,
} = defineProps<PersonalizedFeedBlockProps>()

const { FF_DISPLAY_PERSONALIZED_FEED_DEBUG_INFO = false } =
  useRuntimeConfig().public

// This event is common to every CMS block in order to hide the block containers
// on demand
defineEmits(['error', 'reset-error', 'loaded'])

const tabItem = useTemplateRef('tabItem')
const scrollerRef = useTemplateRef<HTMLDivElement>('tabs-scroller')
const { maskClass } = useScrollMaskClass(scrollerRef)

const { cards, source, pending } = usePersonalizedFeed({
  feed,
  productQuery,
  fallback,
  title: title || '',
  blockPosition,
})

const { activeTabIndex, setActiveTabIndex, goToPreviousTab, goToNextTab } =
  useActiveTabIndex(cards)

const { trackBlockImpression, trackClick } = useTracking()

const contentCardPanelRef = useTemplateRef('content-card-panel')

const hydrationCompleted = ref(false)

const currentContentCard = computed(() => {
  if (!hydrationCompleted.value) {
    return null
  }

  if (!cards.value || cards.value.length === 0) {
    return null
  }

  return cards.value[activeTabIndex.value]
})

const mainTitle = computed(() => {
  // if forceSingleContent, we display the title of the first card (if it exists)
  if (forceSingleContent) {
    return cards.value?.[0]?.title || title
  }

  return title
})

onMounted(async () => {
  hydrationCompleted.value = true
})

function onTabClick(i: number) {
  setActiveTabIndex(i)
  requestAnimationFrame(() => {
    const targetTab = tabItem.value?.find(
      (el) => el?.$attrs.id === `tab-${i}`,
    )?.$el
    if (targetTab) {
      targetTab.focus()
      targetTab.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      })
    }
  })
  trackClick({
    ...tracking,
    position: i + 1,
    blockTitle: title || '',
    tabName: cards.value ? cards.value[i]?.title : '',
    source: source.value || '',
  })
}

// // send an block_impression event when the whole block enters the viewport and the first tab has been loaded
function trackPersonalizedFeedImpressionWithFirstTabInfo() {
  trackBlockImpression({
    ...tracking,
    blockTitle: title || '',
    tabs: cards.value?.map((c) => c.title) || [],
    source: source.value || '',
    tabName: cards.value ? cards.value[activeTabIndex.value]?.title : '',
  })
}

const { isVisible } = useIsVisible({
  targetElement: contentCardPanelRef,
  onIntersection: ({ visible }) => {
    if (visible && !pending.value) {
      trackPersonalizedFeedImpressionWithFirstTabInfo()
    }
  },
})

watch(pending, () => {
  if (!pending.value && isVisible.value) {
    trackPersonalizedFeedImpressionWithFirstTabInfo()
  }
})
</script>
